import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import { useAppSelector } from '../store';
import { fetchAllStorageKeyPrefix } from '../services/data/axios-base';
import { selectOrganizationsData } from '../store/organization/organization-slice-selectors';
import OrganizationRole from '../models/enums/organization-roles';

export type FetchAllPage = 'robots' | 'missions' | 'dashboard';

const readStored = (storageKey: string): boolean | null => {
  try {
    const value = localStorage.getItem(storageKey);
    return value == null ? null : value === 'true';
  } catch {
    return null;
  }
};

/**
 * Whether to include everything from sub organizations on the given page.
 *
 * The URL param wins when present, so shared links keep working, but it never touches the saved
 * choice. Toggling is the only thing that saves: it holds per organization and page until toggled
 * again or until a filter reset clears it. Without either, it starts on, except when viewing a
 * manufacturer organization.
 */
const useFetchAll = (
  page: FetchAllPage,
): {
  fetchAll: boolean;
  saveFetchAll: (value: boolean) => void;
  clearFetchAll: () => void;
} => {
  const [filterParams] = useSearchParams();
  const viewedOrganization = useAppSelector((state) => selectOrganizationsData(state).current);
  const storageKey = `${fetchAllStorageKeyPrefix}:${page}:${viewedOrganization?.id ?? ''}`;
  const [stored, setStored] = useState(() => readStored(storageKey));

  useEffect(() => {
    setStored(readStored(storageKey));
  }, [storageKey]);

  const saveFetchAll = useCallback(
    (value: boolean) => {
      try {
        localStorage.setItem(storageKey, value.toString());
      } catch {
        // localStorage unavailable (private mode/quota) — the choice then only lasts this session.
      }
      setStored(value);
    },
    [storageKey],
  );

  const clearFetchAll = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // localStorage unavailable — there is nothing saved to clear.
    }
    setStored(null);
  }, [storageKey]);

  const queryValue = filterParams.get(FilterKeys.All);
  const isManufacturer = viewedOrganization?.role === OrganizationRole.MANUFACTURER;
  const fetchAll = queryValue != null ? queryValue === 'true' : (stored ?? !isManufacturer);

  return { fetchAll, saveFetchAll, clearFetchAll };
};

export default useFetchAll;
